import {
  DetectorPolicyBucket,
  DetectorPolicyToolKey,
  DetectorWordsLimits,
  LegacyDetectorLimitsResponse,
  PolicyLimitBucketsByUnit,
  ToolPolicyResponse,
  ToolPolicySubFeature,
  ToolPolicyTool,
} from '~/services/toolPolicy/types';
import {
  addSubFeatureToBucket,
  createEmptyPolicyLimitBuckets,
  getOrCreateBucketsForUnit,
} from '~/services/toolPolicy/utils/policyBuckets';

const WORDS_UNIT = 'words';

type BucketTarget = 'privateBucket' | 'sharedBucket';

type ClassifiedSubFeature = {
  unit: string;
  target: BucketTarget;
};

type BucketAccumulator = {
  bucketsByUnit: PolicyLimitBucketsByUnit;
  usedSharedFeatureKeys: Set<string>;
  hasKeylessSharedFeature: boolean;
};

export const mapLegacyDetectorLimits = (
  item: LegacyDetectorLimitsResponse,
): DetectorWordsLimits => {
  const regularWordsTotal = item.regular_words_total || 0;
  const regularWordsLeft = item.regular_words_left || 0;
  const extraWordsTotal = item.extra_words_total || 0;
  const extraWordsLeft = item.extra_words_left || 0;

  return {
    words_total: regularWordsTotal + extraWordsTotal,
    words_left: regularWordsLeft + extraWordsLeft,
    freemium_words_total: item.freemium_words_total || 0,
    freemium_words_left: item.freemium_words_left || 0,
    regular_words_total: regularWordsTotal,
    regular_words_left: regularWordsLeft,
    extra_words_total: extraWordsTotal,
    extra_words_left: extraWordsLeft,
  };
};

export const mapDetectorPolicyLimits = (
  policy: ToolPolicyResponse | null | undefined,
  toolKey: DetectorPolicyToolKey,
): DetectorWordsLimits | null => {
  const bucketsByUnit = mapToolPolicyLimitBuckets(policy, toolKey);

  if (!bucketsByUnit) {
    return null;
  }

  const { privateBucket, sharedBucket } =
    bucketsByUnit[WORDS_UNIT] || createEmptyPolicyLimitBuckets();

  if (!privateBucket.entries && !sharedBucket.entries) {
    return null;
  }

  return {
    words_total: privateBucket.total + sharedBucket.total,
    words_left: privateBucket.left + sharedBucket.left,
    freemium_words_total: 0,
    freemium_words_left: 0,
    regular_words_total: privateBucket.total,
    regular_words_left: privateBucket.left,
    extra_words_total: sharedBucket.total,
    extra_words_left: sharedBucket.left,
  };
};

export const mapToolPolicyLimitBuckets = (
  policy: ToolPolicyResponse | null | undefined,
  toolKey: DetectorPolicyToolKey,
): PolicyLimitBucketsByUnit | null => {
  if (policy?.policy_managed !== true) {
    return null;
  }

  const accumulator = createBucketAccumulator();

  policy.tools?.forEach((tool) => {
    collectToolBuckets(accumulator, tool, toolKey);
  });

  if (accumulator.hasKeylessSharedFeature) {
    return null;
  }

  return accumulator.bucketsByUnit;
};

const createBucketAccumulator = (): BucketAccumulator => ({
  bucketsByUnit: {},
  usedSharedFeatureKeys: new Set<string>(),
  hasKeylessSharedFeature: false,
});

const collectToolBuckets = (
  accumulator: BucketAccumulator,
  tool: ToolPolicyTool,
  toolKey: DetectorPolicyToolKey,
): void => {
  const isCurrentTool = isPolicyTool(tool, toolKey);

  tool.sub_features?.forEach((subFeature) => {
    const classification = classifySubFeature(
      subFeature,
      toolKey,
      isCurrentTool,
    );

    if (!classification) {
      return;
    }

    const buckets = getOrCreateBucketsForUnit(
      accumulator.bucketsByUnit,
      classification.unit,
    );

    if (classification.target === 'privateBucket') {
      addSubFeatureToBucket(buckets.privateBucket, subFeature);
      return;
    }

    addSharedSubFeatureToBucket(
      accumulator,
      buckets.sharedBucket,
      classification.unit,
      subFeature,
    );
  });
};

const classifySubFeature = (
  subFeature: ToolPolicySubFeature,
  toolKey: DetectorPolicyToolKey,
  isCurrentTool: boolean,
): ClassifiedSubFeature | null => {
  if (!subFeature.unit) {
    return null;
  }

  const sharedWith = subFeature.shared_with || [];

  if (sharedWith.length > 0) {
    if (!sharedWith.includes(toolKey)) {
      return null;
    }

    return {
      unit: subFeature.unit,
      target: 'sharedBucket',
    };
  }

  if (!isCurrentTool) {
    return null;
  }

  return {
    unit: subFeature.unit,
    target: 'privateBucket',
  };
};

const addSharedSubFeatureToBucket = (
  accumulator: BucketAccumulator,
  bucket: DetectorPolicyBucket,
  unit: string,
  subFeature: ToolPolicySubFeature,
): void => {
  const featureKey = getSharedFeatureKey(unit, subFeature);

  if (!featureKey) {
    accumulator.hasKeylessSharedFeature = true;
    return;
  }

  if (accumulator.usedSharedFeatureKeys.has(featureKey)) {
    return;
  }

  accumulator.usedSharedFeatureKeys.add(featureKey);
  addSubFeatureToBucket(bucket, subFeature);
};

const isPolicyTool = (
  tool: ToolPolicyTool,
  toolKey: DetectorPolicyToolKey,
): boolean => {
  return [tool.key, tool.id, tool.slug, tool.name].includes(toolKey);
};

const getSharedFeatureKey = (
  unit: string,
  subFeature: ToolPolicySubFeature,
): string | null => {
  return subFeature.key ? `${unit}:${subFeature.key}` : null;
};
